import {shouldSuppressNotification} from 'BenchlingUtil';
import * as ChatActions from 'llm/chat-library/ChatActions';
import {openChat} from 'llm/chat-library/sidepanel/ChatRouterUtil';
import {getChatTypeForNewAgentChat, getDefaultAgentType} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import type {ChatUseCase} from 'llm/chat-library/use-cases/chatUseCaseCatalog';
import recordChatUseCaseRun from 'llm/chat-library/use-cases/recordChatUseCaseRun';
import notifications from 'notifications';
import {warn} from 'util/Logging';

/**
 * Start a new chat with the use case's prompt already submitted, record the run, and open the chat.
 *
 * Use case prompts are written to be sent as-is ("Ask me one or two questions…"), so this submits
 * rather than pre-filling a draft. Opening the chat also closes the chat settings modal, since
 * `openChat` clears the modal state.
 */
export default async function runChatUseCase(
  chatUseCase: ChatUseCase,
  {categoryId}: {categoryId: string}
): Promise<void> {
  const agentType = getDefaultAgentType();
  if (agentType == null) {
    notifications.warn('You do not have access to chat. Contact your administrator.');
    return;
  }

  const chatType = getChatTypeForNewAgentChat(agentType);
  const {chatSessionId} = await ChatActions.createChat(chatType, {}, agentType);
  ChatActions.setDraftMessage(chatSessionId, {
    status: ChatActions.ChatDraftStatus.DRAFT,
    message: {text: chatUseCase.prompt, selectedFiles: []},
    settings: {agentType, publicDataEnabled: false},
  });
  ChatActions.submitDraftMessage(chatSessionId, {analytics: {messageCount: 0}});
  recordChatUseCaseRun(chatUseCase.id, {categoryId, source: 'use_cases_tab', campaign: null});
  openChat(chatSessionId);
}

/**
 * Report a use case that could not be started, for every surface that starts one.
 *
 * Chat creation already surfaces insufficient credits with its own persistent warning and marks the
 * error. A second, generic "try again" would both duplicate it and misadvise: a retry cannot succeed
 * until the tenant has credits.
 */
export function notifyChatUseCaseRunFailed(e: unknown, {chatUseCaseId}: {chatUseCaseId: string}): void {
  if (shouldSuppressNotification(e as {__suppress_notification?: boolean})) {
    return;
  }
  warn('[llm] Failed to run use case', {extra: {chatUseCaseId, error: e}});
  notifications.error('Could not start a chat for this use case. Try again.');
}
